import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import {
  useKbItems,
  type KbItemRow,
  type KbProduct,
  type KbSummary,
  type KbSummaryModule,
} from '@/lib/api/vendor-knowledge';
import { RedactedText } from '@/components/ui/citation-chip';
import { cn } from '@/lib/utils/cn';
import { Chip, Count, Linkish, MonoNo, Section, SubHead, type ChipTone } from '../kb-bits';
import { ANSWER_KIND, fmt, plural, possessive } from '../kb-logic';

/**
 * Requirement answers (prototype `answersSection()` / `ansTable()`): each
 * implementation partner's own answer from Caliber v1, by library module
 * and requirement, with its answer code and comment. Closed until opened;
 * a module's answers are fetched when it is opened. Counts are the
 * server's (`GET /summary` modules).
 */

export type AnswersScope = { kind: 'vendor' | 'partner'; id: string; name?: string };

const SHOWN = 25;

export function AnswersSection({
  scope,
  summary,
  products,
  defaultOpen = false,
}: {
  scope: AnswersScope;
  summary: KbSummary;
  /** The products to group by, in order. */
  products: Array<Pick<KbProduct, 'id' | 'name'> & { vendorName?: string }>;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = React.useState(defaultOpen);
  const modules = (summary.modules ?? []).filter((m) => m.requirement_answers > 0);
  const byProduct = products
    .map((p) => ({ product: p, modules: modules.filter((m) => m.product_id === p.id) }))
    .filter((g) => g.modules.length > 0);
  const several = byProduct.length > 1 || scope.kind === 'partner';
  const what =
    scope.kind === 'vendor'
      ? `${fmt(summary.answered_requirements)} requirements answered by ${plural(summary.answer_partners, 'implementation partner')}`
      : `${fmt(summary.answered_requirements)} requirements answered`;
  return (
    <Section id="kb-answers" label="Requirement answers">
      <h2 className="m-0">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="flex w-full items-center gap-2.5 border-0 bg-transparent p-0 text-left hover:[&>span:first-child]:underline hover:[&>span:first-child]:underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-amber-600"
        >
          <span className="text-h2 font-semibold text-ink-900">Requirement answers</span>
          <Count>{summary.requirement_answers}</Count>
          <span className="min-w-0 flex-1 text-meta font-normal text-ink-500">
            {what} · Avero Advisors Vendor Knowledge Base
          </span>
          <ChevronDown
            className={cn('h-4 w-4 flex-none text-ink-300', open && 'rotate-180')}
            aria-hidden="true"
          />
        </button>
      </h2>
      {open ? (
        <>
          <div className="mb-3 mt-1.5 text-meta text-ink-500">
            {scope.kind === 'vendor'
              ? 'Each answer is a partner’s own words from Caliber v1, linked to the requirement-library item with the same number.'
              : `Grouped by product. Each is ${possessive(scope.name ?? 'the partner')} own words from Caliber v1.`}
          </div>
          {byProduct.map((g) => (
            <div key={g.product.id} className="mt-4 first:mt-0">
              {several ? (
                <SubHead
                  count={g.modules.reduce((n, m) => n + m.requirement_answers, 0)}
                  meta={g.product.vendorName}
                >
                  {g.product.name}
                </SubHead>
              ) : null}
              {g.modules.map((m) => (
                <ModuleAnswers
                  key={`${m.product_id}:${m.library_module_id ?? m.label}`}
                  module={m}
                  scope={scope}
                  showPartner={scope.kind === 'vendor'}
                />
              ))}
            </div>
          ))}
        </>
      ) : null}
    </Section>
  );
}

function ModuleAnswers({
  module: m,
  scope,
  showPartner,
}: {
  module: KbSummaryModule;
  scope: AnswersScope;
  showPartner: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="mb-2.5 overflow-hidden rounded-card border border-ink-100 bg-paper-0">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2.5 border-0 bg-paper-50 px-3.5 py-2.5 text-left text-body-sm hover:bg-paper-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-accent-amber-600"
      >
        <ChevronDown
          className={cn('h-3.5 w-3.5 flex-none text-ink-300', !open && '-rotate-90')}
          aria-hidden="true"
        />
        <span className="font-semibold text-ink-900">{m.label ?? 'Other'}</span>
        <span className="text-meta text-ink-500">
          {plural(m.answered_requirements, 'requirement')}
        </span>
        <span className="flex-1" />
        <Count>{m.requirement_answers}</Count>
      </button>
      {open ? <AnswersTable module={m} scope={scope} showPartner={showPartner} /> : null}
    </div>
  );
}

const ANSWER_TONE: Record<string, ChipTone> = {
  S: 'healthy',
  C: 'neutral',
  T: 'unassessed',
  N: 'critical',
  F: 'at-risk',
};

export function AnswerChip({ code, label }: { code: string | null; label: string | null }) {
  if (!code) return <Chip tone="frozen">{label ?? 'No answer code'}</Chip>;
  return (
    <Chip tone={ANSWER_TONE[code.toUpperCase()] ?? 'neutral'} title={`Answer code ${code}`}>
      <span className="tabular-nums">
        {code} · {label}
      </span>
    </Chip>
  );
}

function AnswersTable({
  module: m,
  scope,
  showPartner,
}: {
  module: KbSummaryModule;
  scope: AnswersScope;
  showPartner: boolean;
}) {
  const items = useKbItems({
    product_id: m.product_id ?? undefined,
    library_module_id: m.library_module_id ?? undefined,
    kind: ANSWER_KIND,
    status: 'confirmed',
    ...(scope.kind === 'partner' ? { partner_id: scope.id } : {}),
  });
  const [all, setAll] = React.useState(false);
  // A module's answers are read in full, then ordered by requirement number.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = items;
  React.useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const rows = React.useMemo(() => {
    const list = (items.data?.pages.flatMap((p) => p.items) ?? []).filter(
      (r) => m.library_module_id !== null || !r.module?.id,
    );
    return [...list].sort(
      (a, b) =>
        (a.requirement?.library_item_number ?? '').localeCompare(
          b.requirement?.library_item_number ?? '',
          'en',
          { numeric: true },
        ) || (a.partner?.name ?? '').localeCompare(b.partner?.name ?? ''),
    );
  }, [items.data, m.library_module_id]);

  if (items.isPending)
    return <div className="px-3.5 py-2.5 text-meta text-ink-500">Loading answers…</div>;
  const shown = all ? rows : rows.slice(0, SHOWN);
  let prev: string | null = null;
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[480px] table-fixed border-collapse bg-paper-0">
          <colgroup>
            <col />
            <col className="w-[160px]" />
            <col className="w-[40%]" />
          </colgroup>
          <thead>
            <tr>
              {['Requirement', showPartner ? 'Answer and partner' : 'Answer', 'Response'].map(
                (h) => (
                  <th
                    key={h}
                    scope="col"
                    className="border-b border-ink-100 bg-paper-100 px-3 py-2 text-left text-meta font-medium text-ink-500"
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const req = r.requirement?.library_item_id ?? r.id;
              const cont = req === prev;
              prev = req;
              return <AnswerRow key={r.id} row={r} cont={cont} showPartner={showPartner} />;
            })}
          </tbody>
        </table>
      </div>
      {rows.length > shown.length ? (
        <Linkish className="mx-3.5 mb-3 mt-2.5" onClick={() => setAll(true)}>
          Show all {fmt(rows.length)}
        </Linkish>
      ) : items.hasNextPage ? (
        <div className="px-3.5 py-2 text-meta text-ink-500">Loading more…</div>
      ) : null}
    </>
  );
}

/** Whether an answer has a comment, or only its code (the excerpt then is the code). */
function commentOf(r: KbItemRow): string | null {
  const code = r.requirement?.answer_code?.trim().toUpperCase();
  const text = r.excerpt.trim();
  return code && text.toUpperCase() === code ? null : text;
}

function AnswerRow({
  row: r,
  cont,
  showPartner,
}: {
  row: KbItemRow;
  cont: boolean;
  showPartner: boolean;
}) {
  const comment = commentOf(r);
  const td = 'px-3 py-[9px] align-top text-body-sm leading-5 text-ink-700';
  return (
    <tr className={cn('hover:bg-paper-50', cont ? '' : 'border-t border-ink-100')}>
      <td className={cn(td, cont && 'pt-0')}>
        {cont ? null : (
          <>
            {r.requirement?.library_item_number ? (
              <MonoNo className="mr-1">{r.requirement.library_item_number}</MonoNo>
            ) : null}
            <span className="text-ink-900">{r.requirement?.library_text ?? r.statement}</span>
          </>
        )}
      </td>
      <td className={cn(td, cont && 'pt-0')}>
        <AnswerChip
          code={r.requirement?.answer_code ?? null}
          label={r.requirement?.answer_label ?? null}
        />
        {showPartner ? (
          <div className="mt-1 text-meta text-ink-700">
            {r.partner?.name ?? 'The vendor itself'}
          </div>
        ) : null}
      </td>
      <td className={cn(td, cont && 'pt-0')}>
        {comment ? (
          <span className="font-serif text-[14px] text-ink-900">
            <RedactedText text={comment} />
          </span>
        ) : (
          <span className="text-meta text-ink-300">No comment given</span>
        )}
      </td>
    </tr>
  );
}
